import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Badge,
  Modal,
  TextInput,
  Button,
  Select,
  Spinner,
} from "flowbite-react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  rectIntersection,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { FaGripVertical, FaFilter, FaMagnifyingGlass } from "react-icons/fa6";
import { Tooltip } from "react-tooltip";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HiClock,
  HiPlus,
  HiUser,
  HiPhone,
  HiVideoCamera,
  HiMail,
  HiUserGroup,
  HiRefresh,
  HiMinus,
  HiChatAlt2,
  HiAnnotation,
  HiSearch,
} from "react-icons/hi";
import {
  HiMiniMagnifyingGlassMinus,
  HiMiniMagnifyingGlassPlus,
} from "react-icons/hi2";
import axios from "../../config/axiosInstance";
import { ADMIN, CRM, LEADS, USERS } from "../../config/apiConfig";
import AutoCompleteInput from "../inputs/Autocomplete";
import QuickCreateCustomer from "../forms/QuickCreateCustomer";
import TaskModal from "../modals/TaskModal";
import { useApplication } from "../../contexts/ApplicationContext";
import FunnelTemplateModal from "../modals/FunnelTemplateModal";
import ClientQuickFilter from "../inputs/ClientQuickFilter";
import FunnelSelectorPro from "./FunnelSelectorPro";

// --- Helpers de moeda / soma por etapa ---

// ===== Funil ativo no localStorage (por usuário) =====
const ACTIVE_FUNNEL_KEY = (userId) => `crmActiveFunnel:${userId || "anon"}`;

function loadActiveFunnel(userId) {
  try {
    return localStorage.getItem(ACTIVE_FUNNEL_KEY(userId)) || null;
  } catch {
    return null;
  }
}

function saveActiveFunnel(userId, funnelId) {
  try {
    if (!funnelId) return;
    localStorage.setItem(ACTIVE_FUNNEL_KEY(userId), String(funnelId));
  } catch {}
}

const normalizeId = (value) => (value == null ? null : String(value));
const normalizeCategory = (value) => String(value || "").trim();
const normalizeIdList = (values) => {
  if (!values) return [];
  if (Array.isArray(values)) return values.map(normalizeId).filter(Boolean);
  if (typeof values === "string") {
    return values
      .split(",")
      .map((item) => normalizeId(item))
      .filter(Boolean);
  }
  return [];
};

const collectFunnelVisibleUserIds = (funnel) => {
  if (!funnel) return [];
  if (Array.isArray(funnel.visibleUserIds) && funnel.visibleUserIds.length)
    return funnel.visibleUserIds;
  if (Array.isArray(funnel.allowedUserIds) && funnel.allowedUserIds.length)
    return funnel.allowedUserIds;
  if (Array.isArray(funnel.ownerUserIds) && funnel.ownerUserIds.length)
    return funnel.ownerUserIds;
  if (funnel.ownerUserId) return [funnel.ownerUserId];
  return [];
};

function bumpCardLocally(stageCards = [], cardId) {
  const cards = stageCards.map((c) => ({ ...c })); // copia rasa
  const idx = cards.findIndex((c) => c.id === cardId);
  if (idx === -1) return stageCards;

  const target = cards[idx];
  const others = cards
    .filter((c) => c.id !== cardId)
    .sort((a, b) => {
      const pa = Number(a.priority) || 999;
      const pb = Number(b.priority) || 999;
      if (pa !== pb) return pa - pb;
      return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
    });

  return [target, ...others].map((c, index) => ({
    ...c,
    priority: index + 1,
  }));
}

const formatBRL = (n) => {
  const v = Number(n || 0);
  try {
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  } catch {
    return `R$ ${v.toFixed(2)}`.replace(".", ",");
  }
};

const getCardValue = (card) =>
  Number(card?.dealValue ?? card?.value ?? card?.amount ?? 0);

const getApprovalAmount = (approval) =>
  Number(
    approval?.amount ??
      approval?.approvedValue ??
      approval?.approvedAmount ??
      approval?.approved_value ??
      approval?.approved_amount ??
      approval?.value ??
      0
  );

const getCardApprovedValue = (card) => {
  const approvals = Array.isArray(card?.dealApprovals)
    ? card.dealApprovals
    : Array.isArray(card?.approvals)
    ? card.approvals
    : [];

  const sumApprovals = approvals.reduce((sum, approval) => {
    if (approval?.approved === false) return sum;
    const amount = getApprovalAmount(approval);
    return Number.isFinite(amount) ? sum + amount : sum;
  }, 0);

  if (sumApprovals > 0) return sumApprovals;

  const direct = Number(
    card?.approvedValue ??
      card?.approvedAmount ??
      card?.approved_value ??
      card?.approved_amount ??
      0
  );
  return Number.isFinite(direct) ? direct : 0;
};

const sumApprovedValue = (cards = []) =>
  cards.reduce((acc, c) => {
    const value = getCardApprovedValue(c);
    return Number.isFinite(value) ? acc + value : acc;
  }, 0);

// ===== Persistência de filtros no localStorage (por usuário e por funil) =====
const FILTERS_KEY = (userId, funnelId) =>
  `crmFilters:${userId || "anon"}:${funnelId || "all"}`;

function loadSavedFilters(userId, funnelId) {
  try {
    const raw = localStorage.getItem(FILTERS_KEY(userId, funnelId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function saveFilters(userId, funnelId, filters) {
  try {
    localStorage.setItem(
      FILTERS_KEY(userId, funnelId),
      JSON.stringify(filters)
    );
  } catch {}
}

function FloatingZoomControls({
  value, // número 0.6..1.6
  onIn,
  onOut,
  onReset,
  min = 0.6,
  max = 1.6,
}) {
  const pct = Math.round(value * 100);
  const radius = 15; // círculo do gauge
  const circ = 2 * Math.PI * radius;
  const clamped = Math.max(min, Math.min(max, value));
  const progress = (clamped - min) / (max - min); // 0..1
  const dash = Math.max(0.02, Math.min(0.98, progress)) * circ;

  return (
    <>
      {/* painél ao lado do FAB: ajuste o right conforme o tamanho do seu botão flutuante */}
      <div className="fixed bottom-6 right-[92px] z-50">
        <div
          className={[
            "flex items-center gap-2 rounded-2xl",
            "border border-white/40 bg-white/70 backdrop-blur-md",
            "shadow-md",
            "px-2.5 py-0.5",
          ].join(" ")}
        >
          {/* Botão - */}
          <button
            data-tooltip-id="zoom-float-tip"
            data-tooltip-content="Diminuir (Ctrl/Cmd + '-')"
            onClick={onOut}
            disabled={value <= min}
            className={[
              "h-9 w-9 rounded-xl border transition-all",
              "bg-white hover:bg-gray-50 active:scale-[.98]",
              "border-gray-200 text-gray-700 flex flex-col items-center justify-center",
              value <= min ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <HiMiniMagnifyingGlassMinus />
          </button>

          {/* Gauge + indicador (clicável para reset) */}
          <button
            onClick={onReset}
            data-tooltip-id="zoom-float-tip"
            data-tooltip-content="Resetar (Ctrl/Cmd + '0')"
            className={[
              "relative h-12 w-12 rounded-full",
              "bg-gradient-to-br from-slate-50 to-white",
              "border border-gray-200",
              "shadow-inner hover:shadow transition-all",
            ].join(" ")}
          >
            {/* percentual no centro */}
            <span className="relative z-10 text-[10px] font-bold text-gray-800 flex flex-col justify-center items-center">
              {pct}%
            </span>
          </button>

          {/* Botão + */}
          <button
            data-tooltip-id="zoom-float-tip"
            data-tooltip-content="Aumentar (Ctrl/Cmd + '+')"
            onClick={onIn}
            disabled={value >= max}
            className={[
              "h-9 w-9 rounded-xl border transition-all",
              "bg-white hover:bg-gray-50 active:scale-[.98]",
              "border-gray-200 text-gray-700 flex flex-col items-center justify-center",
              value >= max ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <HiMiniMagnifyingGlassPlus />
          </button>
        </div>
      </div>
    </>
  );
}

/* ===================== Loader ===================== */
function PageLoader({
  title = "Carregando seu funil...",
  subtitle = "Buscando etapas, cards e filtros",
}) {
  return (
    <div className="w-full flex items-center justify-center p-4">
      <div className="relative w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
        <div className="pointer-events-none absolute -top-16 -left-16 h-48 w-48 rounded-full bg-gradient-to-br from-red-200 via-purple-200 to-pink-200 blur-2xl opacity-60" />
        <div className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-gradient-to-br from-blue-200 via-cyan-200 to-teal-200 blur-2xl opacity-60" />
        <div className="relative z-10 p-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
              <Spinner aria-label="Carregando..." size="xl" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </div>
          </div>
          <div className="mt-6 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-red-500 via-purple-500 to-pink-500 animate-loader-bar" />
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="col-span-1 space-y-2">
              <div className="h-3 w-3/4 rounded bg-gray-100 animate-pulse" />
              <div className="h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
            </div>
            <div className="col-span-2 flex gap-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-6 w-16 rounded-full bg-gray-100 animate-pulse"
                />
              ))}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, c) => (
              <div
                key={c}
                className="rounded-2xl border border-gray-200 bg-gray-50 p-3"
              >
                <div className="h-4 w-2/3 rounded bg-gray-200/70 animate-pulse" />
                <div className="mt-3 space-y-2">
                  {[...Array(4)].map((__, r) => (
                    <div
                      key={r}
                      className="h-16 rounded-xl bg-white border border-gray-200 animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes loader-bar {
          0% { transform: translateX(-120%); }
          50% { transform: translateX(20%); }
          100% { transform: translateX(200%); }
        }
        .animate-loader-bar { animation: loader-bar 1.5s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

/* ===================== Sortable Card ===================== */
/* ===================== Sortable Card ===================== */
function SortableItem({ task, onClick, dimmed = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task?.id });

  const { stats, loading: statsLoading } = useCardTaskStats(task?.id);
  const urgency = getUrgency(stats);
  const color = urgencyColors[urgency];
  const progress =
    stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  // ➕ dealValue do card
  const dealValue = getCardValue(task);
  const hasDealValue = Number.isFinite(dealValue) && dealValue > 0;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    boxShadow: isDragging ? "0 8px 20px rgba(0,0,0,.15)" : undefined,
    opacity: isDragging ? 0.75 : dimmed ? 0.5 : 1,
  };

  const nextAction = stats.nextAction;
  const nextDueStr = nextAction?.dueDate
    ? String(nextAction.dueDate).slice(0, 10)
    : "";
  const { formattedBR, dueColorClass } = formatDue(nextDueStr);
  const NextIcon = actionIconMap[nextAction?.type] || HiRefresh;
  const nextLabel = nextAction
    ? labelMap[nextAction?.type] || "Próxima ação"
    : stats.total
    ? "Sem próximas ações"
    : "—";

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div
        className={[
          "relative rounded-xl border bg-white p-3 transition-all cursor-pointer",
          "hover:shadow-md",
          color.cardBorder,
        ].join(" ")}
        onClick={() => onClick(task)}
      >
        <div
          className={[
            "absolute left-0 top-0 h-full w-1 rounded-l-xl",
            color.sideStrip,
          ].join(" ")}
        />
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-gray-800 leading-snug truncate pr-2">
              {task?.lead?.name || "Não atribuído"}
            </h3>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-600">
              <HiUser size={13} className="text-gray-400" />
              <span className="truncate">
                {task?.responsible?.name || "Sem responsável"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Tag bolinha */}
            {task?.tag && (
              <span
                className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border"
                style={{
                  background: task?.tag?.color,
                  borderColor: task?.tag?.color,
                }}
                title={task?.tag?.name || "Etiqueta"}
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* Handle */}
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-400 pt-0.5"
              onClick={(e) => e.stopPropagation()}
              title="Arrastar"
            >
              <FaGripVertical size={12} />
            </div>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          <div
            className={[
              "inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border",
              color.badge,
            ].join(" ")}
          >
            <NextIcon size={12} />
            <span className="truncate max-w-[140px]">{nextLabel}</span>
          </div>
          <div className="flex items-center text-[10px]">
            <HiClock size={12} className={`mr-1 ${dueColorClass}`} />
            <span className={dueColorClass}>
              Prazo: {nextDueStr ? formattedBR : "--/--"}
            </span>
          </div>
        </div>

        <div className="mt-2">
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className={["h-full rounded-full", color.progressBar].join(" ")}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="mt-2 flex items-center justify-between text-[10px]">
            <div className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200">
                {statsLoading ? "…" : `${stats.done}/${stats.total}`} concluídas
              </span>
              {stats.overdue > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-700">
                  {stats.overdue} atrasadas
                </span>
              )}
              {stats.today > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700">
                  {stats.today} hoje
                </span>
              )}
            </div>

            {hasDealValue && (
              <span
                className="inline-flex items-center max-w-[140px] truncate gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold bg-indigo-50 border-indigo-200 text-indigo-700"
                title={`Valor do negócio: ${formatBRL(dealValue)}`}
                onClick={(e) => e.stopPropagation()}
              >
                {formatBRL(dealValue)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== Helpers / Hooks ===================== */
const actionIconMap = {
  ligacao: HiPhone,
  reuniao: HiVideoCamera,
  email: HiMail,
  visita: HiUserGroup,
  followup: HiRefresh,
  whatsapp: HiChatAlt2,
  nota: HiAnnotation,
};

const urgencyColors = {
  overdue: {
    sideStrip: "bg-gradient-to-b from-red-500 to-red-300",
    progressBar: "bg-red-400",
    badge: "bg-red-50 border-red-200 text-red-700",
    cardBorder: "border-red-100",
  },
  today: {
    sideStrip: "bg-gradient-to-b from-amber-500 to-amber-300",
    progressBar: "bg-amber-400",
    badge: "bg-amber-50 border-amber-200 text-amber-700",
    cardBorder: "border-amber-100",
  },
  upcoming: {
    sideStrip: "bg-gradient-to-b from-emerald-500 to-emerald-300",
    progressBar: "bg-emerald-400",
    badge: "bg-emerald-50 border-emerald-200 text-emerald-700",
    cardBorder: "border-emerald-100",
  },
  none: {
    sideStrip: "bg-gradient-to-b from-gray-300 to-gray-200",
    progressBar: "bg-gray-300",
    badge: "bg-gray-50 border-gray-200 text-gray-600",
    cardBorder: "border-gray-200",
  },
};

function formatDue(dueDateStr) {
  let dueColorClass = "text-gray-400";
  let formattedBR = "";
  if (dueDateStr) {
    const [y, m, d] = dueDateStr.split("-").map(Number);
    const due = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) dueColorClass = "text-red-600";
    else if (diffDays === 0) dueColorClass = "text-amber-600";
    else dueColorClass = "text-emerald-700";
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    formattedBR = `${dd}/${mm}/${y}`;
  }
  return { dueColorClass, formattedBR };
}

function getUrgency(stats) {
  if (!stats || stats.total === 0) return "none";
  if (stats.overdue > 0) return "overdue";
  if (stats.today > 0) return "today";
  if (stats.open > 0) return "upcoming";
  return "none";
}

/* ===== Cache leve para stats ===== */
const _statsCache = new Map();
const statsBus =
  typeof window !== "undefined" && window?.__CRM_STATS_BUS__
    ? window.__CRM_STATS_BUS__
    : new EventTarget();
if (typeof window !== "undefined") window.__CRM_STATS_BUS__ = statsBus;

function useCardTaskStats(cardId) {
  const [loading, setLoading] = React.useState(false);
  const [stats, setStats] = React.useState({
    total: 0,
    open: 0,
    done: 0,
    overdue: 0,
    today: 0,
    nextAction: null,
  });

  const fetchStats = React.useCallback(async () => {
    if (!cardId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${CRM.ADD_CARD_HISTORY}/${cardId}/tasks`);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      let total = list.length,
        done = 0,
        overdue = 0,
        today = 0;
      const openItems = [];

      for (const t of list) {
        const isDone = t.status === "done" || !!t.completedAt;
        if (isDone) done += 1;
        else openItems.push(t);

        const dStr = t?.dueDate ? String(t.dueDate).slice(0, 10) : null;
        if (dStr && !isDone) {
          const [y, m, d] = dStr.split("-").map(Number);
          const dd = new Date(y, m - 1, d);
          dd.setHours(0, 0, 0, 0);
          const diff = Math.round((dd - now) / (1000 * 60 * 60 * 24));
          if (diff < 0) overdue += 1;
          else if (diff === 0) today += 1;
        }
      }

      const nextOpen = [...openItems].sort((a, b) => {
        const aKey = a?.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bKey = b?.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aKey - bKey;
      })[0];

      const computed = {
        total,
        open: total - done,
        done,
        overdue,
        today,
        nextAction: nextOpen
          ? { type: nextOpen.type, dueDate: nextOpen.dueDate || null }
          : null,
      };

      setStats(computed);
      _statsCache.set(String(cardId), computed);
    } catch (e) {
      // silencia
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  React.useEffect(() => {
    const key = String(cardId || "");
    if (!key) return;
    const cached = _statsCache.get(key);
    if (cached) setStats(cached);
    else fetchStats();
  }, [cardId, fetchStats]);

  React.useEffect(() => {
    if (!cardId) return;
    const handler = (ev) => {
      const target = ev?.detail?.cardId;
      if (target === "*" || target === String(cardId)) {
        _statsCache.delete(String(cardId));
        fetchStats();
      }
    };
    statsBus.addEventListener("invalidate", handler);
    return () => statsBus.removeEventListener("invalidate", handler);
  }, [cardId, fetchStats]);

  return { stats, loading };
}

/* ===================== Column ===================== */
function KanbanColumn({
  title,
  columnId,
  tasks,
  onCardClick,
  isClosing = false,
}) {
  const [scope, setScope] = useState("ALL"); // ALL | PRIVATE | TEAM | PUBLIC
  const { isOver, setNodeRef } = useDroppable({ id: columnId });
  const sortedTasks = useMemo(
    () => [...tasks].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
    [tasks]
  );
  const stageTotal = useMemo(
    () => sumApprovedValue(sortedTasks),
    [sortedTasks]
  );

  return (
    <div
      ref={setNodeRef}
      className={`w-[320px] min-w-[320px] p-4 rounded-2xl shadow-md border flex flex-col h-full transition-colors duration-200 ${
        isOver ? "bg-blue-50 border-blue-400" : "bg-white border-gray-200"
      }`}
    >
      {/* header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b gap-2 shrink-0">
        <h3 className="text-xs uppercase font-bold text-gray-800">{title}</h3>
        <span className="text-xs font-medium text-white bg-gray-500 rounded-full px-2 py-0.5">
          {sortedTasks.length}
        </span>
      </div>

      {sortedTasks.length > 0 && (
        <span
          className="inline-flex mb-2 items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold"
          title={`Total da etapa: ${formatBRL(stageTotal)}`}
        >
          Total da etapa: {formatBRL(stageTotal)}
        </span>
      )}

      {/* conteúdo com scroll próprio */}
      <div className="flex-1 overflow-y-auto overscroll-contain pr-1">
        <SortableContext
          items={sortedTasks.map((task) => task?.id)}
          strategy={verticalListSortingStrategy}
        >
          {sortedTasks.length === 0 ? (
            <div className="flex items-center justify-center text-sm text-gray-400 italic h-full">
              Arraste uma tarefa aqui
            </div>
          ) : (
            <div className="space-y-3">
              {sortedTasks.map((task) => (
                <SortableItem
                  key={task?.id}
                  task={task}
                  onClick={onCardClick}
                  dimmed={isClosing}
                />
              ))}
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}

const labelMap = {
  ligacao: "Ligação",
  reuniao: "Reunião",
  email: "E-mail",
  visita: "Visita",
  followup: "Follow-up",
  whatsapp: "WhatsApp",
  nota: "Nota",
};

function FloatingAddButton({ onClick }) {
  return (
    <>
      <button
        onClick={onClick}
        data-tooltip-id="add-card-tooltip"
        data-tooltip-content="Adicionar novo card"
        className={[
          "fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full",
          "bg-gradient-to-br from-rose-500 to-red-600",
          "shadow-md",
          "text-white flex items-center justify-center",
          "hover:brightness-110 active:scale-[.98] transition-all",
        ].join(" ")}
      >
        <svg width="26" height="26" viewBox="0 0 24 24">
          <path fill="currentColor" d="M11 5h2v14h-2zM5 11h14v2H5z" />
        </svg>
      </button>
    </>
  );
}

/* ===================== CRMTable ===================== */
export default function CRMTable() {
  const { user } = useApplication();
  const location = useLocation();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const navigate = useNavigate();
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const groupedFunnels = useMemo(() => {
    const groups = new Map();
    (funnels || []).forEach((f) => {
      const value = normalizeCategory(f?.category ?? f?.categoryName ?? "");
      const key = value || "__none__";
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: value || "Sem categoria",
          items: [],
        });
      }
      groups.get(key).items.push(f);
    });
    const entries = Array.from(groups.values());
    entries.sort((a, b) => {
      if (a.key === "__none__") return 1;
      if (b.key === "__none__") return -1;
      return a.label.localeCompare(b.label, "pt-BR");
    });
    entries.forEach((group) => {
      group.items.sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "pt-BR")
      );
    });
    return entries;
  }, [funnels]);

  // ====== DnD State (novo) ======
  const [activeId, setActiveId] = useState(null);
  const [activeCard, setActiveCard] = useState(null);

  // ====== Zoom ======
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem("crmBoardZoom");
    const n = Number(saved);
    return Number.isFinite(n) ? Math.min(1.6, Math.max(0.6, n)) : 1;
  });
  const ZMIN = 0.6,
    ZMAX = 1.6,
    ZSTEP = 0.1;
  const setZoomSafely = (val) => {
    const next = Math.round(Math.min(ZMAX, Math.max(ZMIN, val)) * 100) / 100;
    setZoom(next);
    try {
      localStorage.setItem("crmBoardZoom", String(next));
    } catch {}
  };
  const zoomIn = () => setZoomSafely(zoom + ZSTEP);
  const zoomOut = () => setZoomSafely(zoom - ZSTEP);
  const zoomReset = () => setZoomSafely(1);

  const myTeamIds =
    user?.teamIds && Array.isArray(user.teamIds)
      ? user.teamIds.map(String)
      : Array.isArray(user?.Teams)
      ? user.Teams.map((t) => String(t?.id)).filter(Boolean)
      : [];

  const canSeeFunnel = (f) => {
    if (!f) return false;
    const vis = String(f.visibility || "").toUpperCase();
    const currentUserId = normalizeId(user?.id);
    if (vis === "PUBLIC") return true;
    if (vis === "PRIVATE") {
      if (!currentUserId) return false;
      const allowedIds = normalizeIdList(collectFunnelVisibleUserIds(f));
      const ownerId = normalizeId(f.ownerUserId);
      return (
        allowedIds.includes(currentUserId) ||
        (ownerId && ownerId === currentUserId)
      );
    }
    if (vis === "TEAM") {
      return myTeamIds.length > 0 && myTeamIds.includes(String(f.teamId));
    }
    return false;
  };

  useEffect(() => {
    const onKey = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === "=" || e.key === "+") {
        e.preventDefault();
        zoomIn();
      }
      if (e.key === "-") {
        e.preventDefault();
        zoomOut();
      }
      if (e.key === "0") {
        e.preventDefault();
        zoomReset();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  // Create card modal
  const [newCardModalOpen, setNewCardModalOpen] = useState(false);
  const [newCardClientId, setNewCardClientId] = useState("");
  const [newCardResponsible, setNewCardResponsible] = useState("");
  const [newCardStageId, setNewCardStageId] = useState("");
  const [creating, setCreating] = useState(false);

  // Filters
  const [filterClientId, setFilterClientId] = useState("");
  const [filterResponsibleId, setFilterResponsibleId] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterDue, setFilterDue] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tmpFilterClientId, setTmpFilterClientId] = useState("");
  const [tmpFilterResponsibleId, setTmpFilterResponsibleId] = useState("");
  const [tmpFilterSearch, setTmpFilterSearch] = useState("");
  const [tmpFilterDue, setTmpFilterDue] = useState("");

  // Details
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskHistory, setTaskHistory] = useState([]);
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);

  // ====== Sensores com activationConstraint ======
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /* ====== Fetch base ====== */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
        setAvailableUsers(res.data.data.data || []);
      } catch (err) {
        console.error("Erro ao buscar usuários:", err);
      }
    };
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${LEADS.LIST}?itemsPerPage=-1`);
        setAvailableClients(res.data.data.data || []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      }
    };
    fetchUsers();
    fetchClients();
  }, []);

  const fetchFunnels = async () => {
    let hadFunnels = false;
    try {
      setLoading(true);
      const res = await axios.get(`${CRM.FUNNELS}`);
      const all = res.data?.data || [];
      const visible = all.filter(canSeeFunnel);

      setFunnels(visible);
      hadFunnels = visible.length > 0;

      if (hadFunnels) {
        // 1) tenta pegar da URL (?f=)
        const params = new URLSearchParams(location.search);
        const urlFunnelId = params.get("f");

        // 2) tenta pegar do localStorage (último funil usado)
        const savedActiveId = loadActiveFunnel(user?.id);

        // 3) decide qual usar, respeitando visibilidade
        const pick =
          visible.find((f) => String(f.id) === String(urlFunnelId)) ||
          visible.find((f) => String(f.id) === String(savedActiveId)) ||
          visible[0];

        const pickedId = String(pick.id);
        setSelectedFunnelId(pickedId);
        saveActiveFunnel(user?.id, pickedId);
        await fetchFunnel(pickedId);
      }
    } catch (err) {
      console.error("Erro ao listar funis", err);
    } finally {
      if (!hadFunnels) setLoading(false);
    }
  };

  const fetchFunnel = async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${CRM.FUNNEL_DETAILS}/${id}/details`);
      setSelectedFunnel(res.data.data);
      // etapa padrão
      const sts = res?.data?.data?.stages || [];
      if (sts.length) setNewCardStageId(String(sts[0].id));
      else setNewCardStageId("");
    } catch (err) {
      console.error("Erro ao buscar funil", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchFunnels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedFunnelId != null && selectedFunnelId !== "") {
      fetchFunnel(String(selectedFunnelId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFunnelId]);

  // Sincroniza o selectedFunnelId -> URL (?f=)
  useEffect(() => {
    if (!selectedFunnelId) return;
    const params = new URLSearchParams(location.search);
    if (params.get("f") !== selectedFunnelId) {
      params.set("f", selectedFunnelId);
      navigate({ search: params.toString() }, { replace: true });
    }

    saveActiveFunnel(user?.id, selectedFunnelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFunnelId]);

  // Ao mudar de funil, restaura filtros salvos para esse funil/usuário
  useEffect(() => {
    if (!selectedFunnelId || !user?.id) return;
    const saved = loadSavedFilters(user.id, selectedFunnelId);
    if (saved) {
      setFilterClientId(saved.filterClientId || "");
      setFilterResponsibleId(saved.filterResponsibleId || "");
      setFilterSearch(saved.filterSearch || "");
      setFilterDue(saved.filterDue || "");
    }
  }, [selectedFunnelId, user?.id]);

  // Sempre que filtros mudarem, persistir (por usuário e por funil)
  useEffect(() => {
    if (!selectedFunnelId || !user?.id) return;
    saveFilters(user.id, selectedFunnelId, {
      filterClientId,
      filterResponsibleId,
      filterSearch,
      filterDue,
    });
  }, [
    filterClientId,
    filterResponsibleId,
    filterSearch,
    filterDue,
    selectedFunnelId,
    user?.id,
  ]);

  /* ====== Local updates ====== */
  const updateStageCards = (stageId, newCards) => {
    setSelectedFunnel((prev) => ({
      ...prev,
      stages: prev.stages.map((stage) =>
        stage.id === stageId ? { ...stage, cards: newCards } : stage
      ),
    }));
  };

  const updateTask = (updatedCard) => {
    const stage = selectedFunnel?.stages?.find((s) =>
      s.cards?.some((c) => c.id === updatedCard.id)
    );
    if (!stage) return;

    // atualiza os dados do card
    const merged = stage.cards.map((c) =>
      c.id === updatedCard.id ? { ...c, ...updatedCard } : c
    );

    // 🔼 sobe pro topo localmente
    const reordered = bumpCardLocally(merged, updatedCard.id);

    updateStageCards(stage.id, reordered);

    Promise.all(
      reordered.map((card) =>
        updateCardBackend(card.id, {
          stageId: stage.id,
          priority: card.priority,
        })
      )
    ).catch((err) => console.error("Erro ao reordenar prioridades:", err));
  };

  const updateCardBackend = async (cardId, data) => {
    try {
      await axios.put(`${CRM.UPDATE_CARD}/${cardId}`, data);
    } catch (err) {
      console.error("Erro ao atualizar card no backend:", err);
    }
  };

  /* ====== Collision strategy (mais estável) ====== */
  const collisionDetection = (args) => {
    // prioriza onde o mouse está, depois interseção de retângulos, depois centro mais próximo
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    const rect = rectIntersection(args);
    if (rect.length > 0) return rect;
    return closestCenter(args);
  };

  /* ====== DnD Handlers ====== */
  const findStageByCardId = (cardId) =>
    selectedFunnel?.stages?.find((s) => s.cards?.some((c) => c.id === cardId));

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const stage = findStageByCardId(active.id);
    const card = stage?.cards?.find((c) => c.id === active.id);
    setActiveCard(card || null);
  };

  // Move “ao passar” por outras colunas para feedback instantâneo
  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over || !selectedFunnel) return;

    // se estamos sobre um card, over.id é o id do card; se sobre a coluna, é o id da coluna
    const activeStage = findStageByCardId(active.id);
    const overStage =
      selectedFunnel.stages.find((s) =>
        s.cards.some((c) => c.id === over.id)
      ) || selectedFunnel.stages.find((s) => s.id === over.id);

    if (!activeStage || !overStage) return;

    // mesmo estágio: preview de reorder
    if (activeStage.id === overStage.id) {
      const from = activeStage.cards.findIndex((c) => c.id === active.id);
      const to = activeStage.cards.findIndex((c) => c.id === over.id);
      if (from !== to && to !== -1) {
        const reordered = arrayMove(activeStage.cards, from, to).map(
          (c, i) => ({
            ...c,
            priority: i + 1,
          })
        );
        updateStageCards(activeStage.id, reordered);
      }
      return;
    }

    // estágio diferente: remove do antigo e insere no novo (topo)
    const moving = activeStage.cards.find((c) => c.id === active.id);
    if (!moving) return;

    const newSource = activeStage.cards
      .filter((c) => c.id !== active.id)
      .map((c, i) => ({ ...c, priority: i + 1 }));

    // posição alvo: se estiver sobre card, inserir antes dele; senão no topo
    const overIndex = overStage.cards.findIndex((c) => c.id === over.id);
    let newTarget;
    if (overIndex === -1) {
      newTarget = [
        { ...moving, stageId: overStage.id, priority: 1 },
        ...overStage.cards,
      ].map((c, i) => ({ ...c, priority: i + 1 }));
    } else {
      const cloned = [...overStage.cards];
      cloned.splice(overIndex, 0, { ...moving, stageId: overStage.id });
      newTarget = cloned.map((c, i) => ({ ...c, priority: i + 1 }));
    }

    updateStageCards(activeStage.id, newSource);
    updateStageCards(overStage.id, newTarget);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveCard(null);
    if (!over || !selectedFunnel) return;

    const sourceStage = findStageByCardId(active.id);
    const targetStage =
      selectedFunnel.stages.find((s) =>
        s.cards.some((c) => c.id === over.id)
      ) || selectedFunnel.stages.find((s) => s.id === over.id);

    if (!sourceStage || !targetStage) return;

    if (sourceStage.id === targetStage.id) {
      // já reordenamos no onDragOver; só persistir prioridades
      const updated = sourceStage.cards.map((c, i) => ({
        ...c,
        priority: i + 1,
      }));
      try {
        await Promise.all(
          updated.map((card) =>
            updateCardBackend(card.id, {
              stageId: sourceStage.id,
              priority: card.priority,
            })
          )
        );
      } catch (e) {
        console.error(e);
      }
      return;
    }

    // mudou de estágio: persistir ambos
    const srcUpdated = sourceStage.cards.map((c, i) => ({
      ...c,
      priority: i + 1,
      stageId: sourceStage.id,
    }));
    const tgtUpdated = targetStage.cards.map((c, i) => ({
      ...c,
      priority: i + 1,
      stageId: targetStage.id,
    }));

    try {
      await Promise.all([
        ...srcUpdated.map((card) =>
          updateCardBackend(card.id, {
            stageId: sourceStage.id,
            priority: card.priority,
          })
        ),
        ...tgtUpdated.map((card) =>
          updateCardBackend(card.id, {
            stageId: targetStage.id,
            priority: card.priority,
          })
        ),
      ]);
    } catch (e) {
      console.error("Erro ao mover card:", e);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveCard(null);
    // opcional: refetchFunil para garantir consistência se algo saiu do lugar
    // fetchFunnel(selectedFunnelId);
  };

  /* ====== Create Card ====== */
  const handleCreateCard = async () => {
    const targetStage = selectedFunnel?.stages?.find(
      (s) => String(s.id) === String(newCardStageId || "")
    );
    if (!targetStage || !newCardClientId) return;

    try {
      setCreating(true);
      const clientName =
        availableClients.find((c) => c.id == newCardClientId)?.name ||
        String(newCardClientId);

      await axios.post(`${CRM.CREATE_CARD}/${targetStage.id}`, {
        title: clientName,
        leadId: newCardClientId,
        responsibleId: newCardResponsible || null,
        priority: (targetStage.cards?.length || 0) + 1,
      });

      await fetchFunnel(selectedFunnelId);

      setNewCardClientId("");
      setNewCardResponsible("");
      setNewCardModalOpen(false);
    } catch (err) {
      console.error("Erro ao criar card", err);
    } finally {
      setCreating(false);
    }
  };

  /* ====== Detalhes ====== */
  const fetchCardDetails = async (id) => {
    try {
      const res = await axios.get(`${CRM.GET_CARD}/${id}/details`);
      const historyRes = await axios.get(
        `${CRM.GET_CARD_HISTORY}/${id}/history`
      );
      const data = res.data.data;
      setSelectedTask({
        ...data,
        checklist: Array.isArray(data.checklist) ? data.checklist : [],
      });
      setTaskHistory(historyRes.data.data);
    } catch (err) {
      console.error("Erro ao buscar detalhes do card", err);
    }
  };

  const refetchCard = async (cardId) => {
    try {
      const res = await axios.get(`${CRM.GET_CARD}/${cardId}/details`);
      const fresh = res?.data?.data;
      if (!fresh?.id) return;

      setSelectedFunnel((prev) => {
        if (!prev) return prev;

        // 1) localizar stage de origem no estado atual
        const sourceIndex = prev.stages.findIndex((st) =>
          (st.cards || []).some((c) => String(c.id) === String(fresh.id))
        );

        // 2) localizar stage de destino (vindo do backend)
        const targetIndex = prev.stages.findIndex(
          (st) => String(st.id) === String(fresh.stageId)
        );

        if (sourceIndex === -1) return prev;

        // se destino não existir neste board (ex: outro funil/visão), apenas remova do source
        if (targetIndex === -1) {
          const stripped = prev.stages.map((st, i) => {
            if (i !== sourceIndex) return st;
            const newCards = (st.cards || [])
              .filter((c) => String(c.id) !== String(fresh.id))
              .map((c, idx) => ({ ...c, priority: idx + 1 })); // normaliza prioridades
            return { ...st, cards: newCards };
          });
          return { ...prev, stages: stripped };
        }

        // 3) construir novas colunas (imutáveis)
        const nextStages = prev.stages.map((st) => ({
          ...st,
          cards: [...(st.cards || [])],
        }));

        const sameStage = sourceIndex === targetIndex;
        const src = nextStages[sourceIndex];
        const tgt = nextStages[targetIndex];

        // Se não mudou de etapa, apenas atualiza o card mantendo a posição/priority atuais
        if (sameStage) {
          const idx = src.cards.findIndex(
            (c) => String(c.id) === String(fresh.id)
          );
          if (idx === -1) return prev;
          const prevCard = src.cards[idx];
          const safePriority = Number.isFinite(Number(fresh.priority))
            ? Number(fresh.priority)
            : Number.isFinite(Number(prevCard?.priority))
            ? Number(prevCard.priority)
            : idx + 1;

          const merged = {
            ...prevCard,
            ...fresh,
            stageId: fresh.stageId ?? prevCard?.stageId ?? src.id,
            priority: safePriority,
          };

          const newCards = [...src.cards];
          newCards[idx] = merged;
          nextStages[sourceIndex] = { ...src, cards: newCards };
          return { ...prev, stages: nextStages };
        }

        // 3a) remover do source (se existir)
        const moving =
          src.cards.find((c) => String(c.id) === String(fresh.id)) || fresh;
        src.cards = src.cards
          .filter((c) => String(c.id) !== String(fresh.id))
          .map((c, idx) => ({ ...c, priority: idx + 1 })); // normaliza

        // 3b) inserir/atualizar no target
        const without = (tgt.cards || []).filter(
          (c) => String(c.id) !== String(fresh.id)
        );
        // empurra e depois normaliza; se o backend mandou priority, deixamos o sort respeitar:
        const inserted = [...without, { ...moving, ...fresh }].sort(
          (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
        );
        tgt.cards = inserted.map((c, idx) => ({
          ...c,
          stageId: c.stageId ?? tgt.id,
          priority: idx + 1,
        }));

        nextStages[sourceIndex] = { ...src };
        nextStages[targetIndex] = { ...tgt };

        return { ...prev, stages: nextStages };
      });
    } catch (err) {
      console.error("Erro ao refetch do card:", err);
    }
  };

  /* ====== Filters ====== */
  const getDueBucket = (dueDateStr) => {
    if (!dueDateStr) return "none";
    const str = String(dueDateStr).slice(0, 10);
    const [y, m, d] = str.split("-").map(Number);
    const due = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "overdue";
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "tomorrow";
    return "future";
  };

  const cardMatchesFilters = (card) => {
    if (
      filterClientId &&
      String(card?.lead?.id || "") !== String(filterClientId)
    )
      return false;
    if (
      filterResponsibleId &&
      String(card?.responsible?.id || "") !== String(filterResponsibleId)
    )
      return false;
    if (filterDue) {
      const bucket = getDueBucket(card?.dueDate);
      if (bucket !== filterDue) return false;
    }
    if (filterSearch?.trim()) {
      const q = filterSearch.toLowerCase();
      const title = String(card?.title || "").toLowerCase();
      const customer = String(card?.lead?.name || "").toLowerCase();
      if (!title.includes(q) && !customer.includes(q)) return false;
    }
    return true;
  };

  const openFilters = () => {
    setTmpFilterClientId(filterClientId);
    setTmpFilterResponsibleId(filterResponsibleId);
    setTmpFilterSearch(filterSearch);
    setTmpFilterDue(filterDue);
    setFiltersOpen(true);
  };
  const applyFilters = () => {
    setFilterClientId(tmpFilterClientId);
    setFilterResponsibleId(tmpFilterResponsibleId);
    setFilterSearch(tmpFilterSearch);
    setFilterDue(tmpFilterDue);
    setFiltersOpen(false);
  };
  const clearFilters = () => {
    setFilterClientId("");
    setFilterResponsibleId("");
    setFilterSearch("");
    setFilterDue("");
    setFiltersOpen(false);
  };

  const createFunnelFromTemplate = async (tpl) => {
    // monte o payload esperado pelo seu backend
    const payload = {
      name: tpl.name,
      visibility: "PUBLIC",
      stages: tpl.stages.map((name, i) => ({ name, order: i + 1 })),
      closingStageIndex: tpl.closingStageIndex,
    };

    // cria o funil
    const res = await axios.post(`${CRM.FUNNELS}`, payload);
    const created = res?.data?.data;

    // recarrega e seleciona o novo
    await fetchFunnels();
    if (created?.id) {
      setSelectedFunnelId(created.id);
      await fetchFunnel(created.id);
    }

    return created;
  };

  /* ====== Estados ====== */
  if (loading) return <PageLoader />;
  if (!loading && funnels.length === 0) {
    return (
      <>
        <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
          {/* Aura decorativa */}
          <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-rose-500/15 to-amber-400/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-gradient-to-tr from-red-500/15 to-cyan-400/15 blur-3xl" />

          <div className="p-10 md:p-12 text-center relative">
            {/* Badge */}
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-3 py-1 text-xs font-medium text-gray-600 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              Comece seu primeiro funil
            </span>

            {/* Ilustração minimalista */}
            <div className="mx-auto mt-6 h-28 w-28 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 p-[2px] shadow-lg">
              <div className="flex h-full w-full items-center justify-center rounded-2xl bg-white">
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 24 24"
                  className="text-red-600"
                >
                  <path fill="currentColor" d="M3 4h18v2l-7 7v4l-4 2v-6L3 6z" />
                </svg>
              </div>
            </div>

            {/* Títulos */}
            <h3 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
              Nenhum funil por aqui… ainda
            </h3>
            <p className="mx-auto mt-2 max-w-xl text-sm md:text-base text-gray-600">
              Crie um funil para organizar etapas, distribuir cards e acelerar
              suas vendas.
            </p>

            {/* Dicas rápidas */}
            <div className="mx-auto mt-6 max-w-2xl grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
              {[
                {
                  t: "Estruture etapas",
                  d: "Defina o fluxo: Prospecção → Contato → Proposta → Fechamento.",
                },
                {
                  t: "Atribua responsáveis",
                  d: "Distribua cards e acompanhe o avanço por pessoa.",
                },
                {
                  t: "Automatize ações",
                  d: "Lembretes, follow-ups e tarefas do dia na sua agenda.",
                },
              ].map((it, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 bg-white/60 p-4"
                >
                  <div className="mb-1 text-sm font-semibold text-gray-900">
                    {it.t}
                  </div>
                  <div className="text-xs text-gray-600">{it.d}</div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                size="lg"
                onClick={() => setTemplateModalOpen(true)}
                className="bg-primary"
              >
                <span className="inline-flex items-center gap-2">
                  Criar novo funil
                </span>
              </Button>
            </div>
          </div>
        </div>
        <FunnelTemplateModal
          show={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
          onCreateFromTemplate={createFunnelFromTemplate}
        />
      </>
    );
  }
  if (!selectedFunnel) return <PageLoader />;

  const dropAnimation = {
    duration: 200,
    easing: "cubic-bezier(.2,.8,.2,1)",
  };

  return (
    <>
      <div className="relative rounded-xl border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm">
        <div className="px-3 md:px-4 py-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            {/* Esquerda: título + filtros + zoom */}
            <div className="flex items-center flex-wrap gap-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1 shadow-sm">
                <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-gray-900 text-white text-[9px] font-bold leading-none">
                  {String(selectedFunnel?.name || "F")[0]?.toUpperCase()}
                </span>
                <span className="text-[13px] font-semibold text-gray-900 leading-none">
                  Funil:{" "}
                  <span className="font-medium text-gray-700">
                    {selectedFunnel?.name}
                  </span>
                </span>
              </div>

              <Button
                onClick={openFilters}
                color="gray"
                size="xs"
                className="!h-7 !rounded-full !border-gray-200 !bg-white hover:!bg-gray-50 shadow-sm"
              >
                <div className="flex gap-1.5 items-center text-[12px]">
                  <FaFilter />
                  <span>Filtros</span>
                </div>
              </Button>

              {/* 🔎 Autocomplete de clientes (ao lado do zoom) */}
              <div className="hidden md:flex items-center flex-1">
                <ClientQuickFilter
                  options={(availableClients || []).map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  value={filterClientId}
                  onChange={(id) => setFilterClientId(id)}
                  onClear={() => setFilterClientId("")}
                  isLoading={false}
                  className="ml-2"
                />
              </div>
            </div>

            {/* Direita: seletor de funil */}
            <div className="flex items-center gap-2">
              <div className="hidden md:block text-[11px] text-gray-500">
                Funis
              </div>
              <Select
                value={selectedFunnelId ?? ""}
                onChange={(e) => setSelectedFunnelId(e.target.value || null)}
                className="!border-0 !bg-transparent !p-0 !text-sm !max-w-[250px]"
              >
                {groupedFunnels.map((group) => (
                  <optgroup key={group.key} label={group.label}>
                    {group.items.map((f) => (
                      <option key={f.id} value={String(f.id)}>
                        {f.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
              <Button onClick={() => navigate("/funnels")}>Gerenciar</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Chips de filtros ativos (linha única, menor) */}
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {filterClientId && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Cliente:{" "}
            {availableClients.find(
              (c) => String(c.id) === String(filterClientId)
            )?.name || filterClientId}
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterClientId("")}
            >
              ×
            </button>
          </span>
        )}
        {filterResponsibleId && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Resp.:{" "}
            {availableUsers.find(
              (u) => String(u.id) === String(filterResponsibleId)
            )?.name || filterResponsibleId}
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterResponsibleId("")}
            >
              ×
            </button>
          </span>
        )}
        {filterDue && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Prazo:{" "}
            {{
              overdue: "Vencido",
              today: "Hoje",
              tomorrow: "Amanhã",
              future: "Futuro",
              none: "Sem prazo",
            }[filterDue] || filterDue}
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterDue("")}
            >
              ×
            </button>
          </span>
        )}
        {filterSearch && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Busca: “{filterSearch}”
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterSearch("")}
            >
              ×
            </button>
          </span>
        )}
      </div>

      {/* Board + DnD */}
      <DndContext
        key={selectedFunnelId}
        sensors={sensors}
        collisionDetection={collisionDetection}
        modifiers={[restrictToWindowEdges]}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* container com UM único scroll horizontal */}
        <div className="mt-4 h-[calc(100vh-190px)] rounded-xl bg-gray-200 overflow-x-auto overflow-y-hidden">
          {/* wrapper do zoom (compensa altura para permanecer constante) */}
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "0 0",
              width: `${(1 / zoom) * 100}%`,
              height: `calc((100vh - 190px) / ${zoom})`, // 👈 compensação da altura
            }}
          >
            {/* linha das colunas — agora preenche 100% da altura do wrapper */}
            <div className="inline-flex gap-4 pb-4 h-full p-3">
              {selectedFunnel?.stages?.map((stage) => (
                <KanbanColumn
                  key={stage.id}
                  title={stage.name}
                  columnId={stage.id}
                  tasks={(stage.cards || []).filter(cardMatchesFilters)}
                  onCardClick={(task) => fetchCardDetails(task?.id)}
                  isClosing={
                    String(stage.id) ===
                    String(selectedFunnel?.closingStageId || "")
                  }
                />
              ))}
            </div>
          </div>
        </div>

        <DragOverlay dropAnimation={dropAnimation}>
          {activeCard ? (
            <div className="w-[300px]">
              <SortableItem
                task={activeCard}
                onClick={() => {}}
                dimmed={false}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Detalhes do card */}
      {selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          updateTask={updateTask}
          history={taskHistory}
          setHistory={setTaskHistory}
          setSelectedTask={setSelectedTask}
          onRefetchCard={refetchCard}
          stages={selectedFunnel?.stages || []}
          onMoveStage={async (cardId, targetStageId) => {
            // usa mesma lógica de onDragEnd para persistir
            const sourceStage = selectedFunnel?.stages?.find((s) =>
              s.cards?.some((c) => c.id === cardId)
            );
            const targetStage = selectedFunnel?.stages?.find(
              (s) => s.id === targetStageId
            );
            if (
              !sourceStage ||
              !targetStage ||
              sourceStage.id === targetStage.id
            )
              return;

            const moving = sourceStage.cards.find((c) => c.id === cardId);
            const updatedSource = sourceStage.cards
              .filter((c) => c.id !== cardId)
              .map((c, i) => ({
                ...c,
                priority: i + 1,
                stageId: sourceStage.id,
              }));

            const updatedTarget = [
              { ...moving, priority: 1, stageId: targetStage.id },
              ...targetStage.cards.map((c, i) => ({
                ...c,
                priority: i + 2,
                stageId: targetStage.id,
              })),
            ];

            updateStageCards(sourceStage.id, updatedSource);
            updateStageCards(targetStage.id, updatedTarget);

            try {
              await Promise.all([
                ...updatedSource.map((card) =>
                  updateCardBackend(card.id, {
                    stageId: sourceStage.id,
                    priority: card.priority,
                  })
                ),
                ...updatedTarget.map((card) =>
                  updateCardBackend(card.id, {
                    stageId: targetStage.id,
                    priority: card.priority,
                  })
                ),
              ]);
            } catch (e) {
              console.error("Erro ao mover card via modal:", e);
            }
          }}
          onCardDeleted={(cardId) => {
            setSelectedFunnel((prev) => {
              if (!prev) return prev;
              const nextStages = prev.stages.map((st) => {
                const had = (st.cards || []).some(
                  (c) => String(c.id) === String(cardId)
                );
                if (!had) return st;
                const newCards = (st.cards || [])
                  .filter((c) => String(c.id) !== String(cardId))
                  .map((c, i) => ({ ...c, priority: i + 1 }));
                return { ...st, cards: newCards };
              });
              return { ...prev, stages: nextStages };
            });
            setSelectedTask(null);
          }}
        />
      )}

      {/* Botão flutuante */}
      <FloatingAddButton onClick={() => setNewCardModalOpen(true)} />

      <FloatingZoomControls
        value={zoom}
        onIn={zoomIn}
        onOut={zoomOut}
        onReset={zoomReset}
        min={ZMIN}
        max={ZMAX}
      />

      {/* Modal: Criar Card */}
      <Modal show={newCardModalOpen} onClose={() => setNewCardModalOpen(false)}>
        <Modal.Header>
          <div className="text-lg font-semibold text-gray-800">
            Criar novo card
          </div>
        </Modal.Header>
        <Modal.Body className="!overflow-visible">
          <div className="space-y-6">
            <div>
              <AutoCompleteInput
                label="Cliente *"
                name="newCardClient"
                options={(availableClients || []).map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                value={newCardClientId}
                onChange={(opt) => setNewCardClientId(opt?.value || "")}
              />

              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setQuickCustomerOpen(true)}
                  className="text-sm text-blue-700 hover:underline"
                >
                  Não encontrou? Cadastrar novo cliente
                </button>
              </div>

              <QuickCreateCustomer
                show={quickCustomerOpen}
                onClose={() => setQuickCustomerOpen(false)}
                onCreated={(created) => {
                  setAvailableClients((prev) => [created, ...prev]);
                  setNewCardClientId(created.id);
                }}
              />
            </div>

            {/* Etapa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etapa *
              </label>
              <Select
                value={newCardStageId}
                onChange={(e) => setNewCardStageId(e.target.value)}
              >
                {(selectedFunnel?.stages || []).map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsável
              </label>
              <Select
                value={newCardResponsible}
                onChange={(e) => setNewCardResponsible(e.target.value)}
              >
                <option value="">Não atribuído</option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end">
          <Button
            onClick={handleCreateCard}
            disabled={!newCardClientId || !newCardStageId || creating}
            className="w-full sm:w-auto"
          >
            {creating ? "Criando..." : "Criar tarefa"}
          </Button>
          <Button
            color="gray"
            onClick={() => setNewCardModalOpen(false)}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Filtros */}
      <Modal show={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <Modal.Header>
          <div className="text-lg font-semibold text-gray-800">Filtros</div>
        </Modal.Header>
        <Modal.Body className="!overflow-visible">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsável
              </label>
              <Select
                value={tmpFilterResponsibleId}
                onChange={(e) => setTmpFilterResponsibleId(e.target.value)}
              >
                <option value="">Todos</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Prazo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prazo
              </label>
              <Select
                value={tmpFilterDue}
                onChange={(e) => setTmpFilterDue(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="overdue">Vencido</option>
                <option value="today">Hoje</option>
                <option value="tomorrow">Amanhã</option>
                <option value="future">Futuro</option>
                <option value="none">Sem prazo</option>
              </Select>
            </div>

            {/* Busca */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <TextInput
                value={tmpFilterSearch}
                onChange={(e) => setTmpFilterSearch(e.target.value)}
                placeholder="Cliente ou título..."
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-between">
          <Button color="gray" onClick={clearFilters}>
            Limpar
          </Button>
          <div className="flex gap-2">
            <Button color="gray" onClick={() => setFiltersOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={applyFilters}>Aplicar</Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}
